import { generateSecureRandomBytes } from "./cryptoUtils";

/**
 * Interfaccia comune per AES
 * Permette di astrarre l'implementazione specifica dietro un'interfaccia comune,
 * facilitando la sostituzione o l'aggiunta di nuove implementazioni in futuro.
 */
export interface IAES {
    encrypt(data: Uint8Array): Promise<Uint8Array>;
    decrypt(data: Uint8Array): Promise<Uint8Array>;
}

/**
 * Factory per creare istanze di AES
 * Permette di centralizzare la logica di creazione e configurazione delle istanze AES,
 * rendendo più semplice gestire diverse versioni o configurazioni in futuro.
 */
interface AESFactory {
    create(key: Uint8Array, iv: Uint8Array): Promise<IAES>;
}

// #region Implementazioni classi AES
/**
 * AES-256-CBC async-safe
 */
class AES_CBC implements IAES {
    private static readonly algorithmName: string = "AES-CBC";
    private readonly key: CryptoKey;
    private readonly iv: Uint8Array;

    private constructor(key: CryptoKey, iv: Uint8Array) {
        this.key = key;
        this.iv = iv;
    }

    /**
     * Factory async per creare istanza AES
     */
    static async create(keyBytes: Uint8Array, ivBytes: Uint8Array): Promise<AES_CBC> {
        const key = await crypto.subtle.importKey(
            "raw",
            keyBytes as BufferSource,
            { name: AES_CBC.algorithmName },
            false,
            ["encrypt", "decrypt"]
        );
        return new AES_CBC(key, ivBytes);
    }

    async encrypt(data: Uint8Array): Promise<Uint8Array> {
        const enc = await crypto.subtle.encrypt(
            { name: AES_CBC.algorithmName, iv: this.iv as BufferSource },
            this.key,
            data as BufferSource
        );
        return new Uint8Array(enc);
    }

    async decrypt(data: Uint8Array): Promise<Uint8Array> {
        const dec = await crypto.subtle.decrypt(
            { name: AES_CBC.algorithmName, iv: this.iv as BufferSource },
            this.key,
            data as BufferSource
        );
        return new Uint8Array(dec);
    }
}

/**
 * AES-256-GCM async-safe
 */
class AES_GCM implements IAES {
    private static readonly algorithmName: string = "AES-GCM";

    private readonly key: CryptoKey;
    private static readonly nonceLength = 12; // 12 bytes required by spec
    private static readonly tagLength = 128;  // 128 bits = 16 bytes

    private constructor(key: CryptoKey) {
        this.key = key;
    }

    /**
     * Factory async per creare istanza AES
     */
    static async create(keyBytes: Uint8Array): Promise<AES_GCM> {
        // In AES-GCM, il nonce (IV) viene generato casualmente per ogni operazione di cifratura,
        // quindi non è necessario passarlo alla factory. 
        // Verrà generato internamente durante la cifratura.
        const key = await crypto.subtle.importKey(
            "raw",
            keyBytes as BufferSource,
            { name: AES_GCM.algorithmName },
            false,
            ["encrypt", "decrypt"]
        );
        return new AES_GCM(key);
    }

    /**
     * Cifra i dati usando AES-GCM. Genera un nonce casuale per ogni operazione e lo include nel risultato.
     * @param data Dati da cifrare
     * @returns Array combinato di nonce + ciphertext + tag, necessario per la decrittazione.
     */
    async encrypt(data: Uint8Array): Promise<Uint8Array> {
        // Genera un nonce casuale per questa operazione di cifratura
        const nonce: Uint8Array = generateSecureRandomBytes(AES_GCM.nonceLength);

        // Cifra i dati usando AES-GCM con il nonce generato
        const encrypted = await crypto.subtle.encrypt(
            {
                name: AES_GCM.algorithmName,
                iv: nonce as BufferSource,
                tagLength: AES_GCM.tagLength,
            },
            this.key,
            data as BufferSource
        );

        // Il risultato di crypto.subtle.encrypt in AES-GCM include già il tag di autenticazione alla fine del ciphertext,
        // quindi possiamo semplicemente combinare nonce + ciphertext (che include il tag) in un unico array da restituire.
        const ciphertextWithTag = new Uint8Array(encrypted);

        // Combine nonce + ciphertext + tag
        const combined = new Uint8Array(
            nonce.length + ciphertextWithTag.length
        );

        // Copia nonce all'inizio
        combined.set(nonce, 0);
        // Copia ciphertext + tag subito dopo il nonce
        combined.set(ciphertextWithTag, nonce.length);

        // Restituisce l'array combinato (nonce + ciphertext + tag)
        return combined;
    }

    /**
     * Decripta i dati cifrati con AES-GCM. Si aspetta un array che combina nonce + ciphertext + tag.
     * @param data Dati cifrati (nonce + ciphertext + tag)
     * @returns Dati decrittati
     */
    async decrypt(data: Uint8Array): Promise<Uint8Array> {
        if (data.length < AES_GCM.nonceLength + 16) {
            throw new Error("Invalid encrypted payload");
        }

        const nonce = data.slice(0, AES_GCM.nonceLength);
        const ciphertextWithTag = data.slice(AES_GCM.nonceLength);

        try {
            const decrypted = await crypto.subtle.decrypt(
                {
                    name: AES_GCM.algorithmName,
                    iv: nonce,
                    tagLength: AES_GCM.tagLength,
                },
                this.key,
                ciphertextWithTag as BufferSource
            );

            return new Uint8Array(decrypted);
        } catch {
            // Tag verification failed
            throw new Error("Authentication failed (invalid GCM tag)");
        }
    }
}
// #endregion

//#region Lista versioni AES e relative factory
/**
 * Implementazioni specifiche di AES
*/
export enum AESType {
    AES_CBC = "AES-CBC",
    AES_GCM = "AES-GCM",
}

/**
 * Implementazioni specifiche di AES
 */
const factories: Record<AESType, AESFactory> = {
    [AESType.AES_CBC]: AES_CBC,
    [AESType.AES_GCM]: AES_GCM,
};

/**
 * Factory centralizzata per creare istanze AES
 * @param type Tipo AES
 * @param key Chiave
 * @param iv Initialization Vector
 */
export async function getAESInstance(type: AESType, key: Uint8Array, iv: Uint8Array): Promise<IAES> {
    const factory = factories[type];
    if (!factory) throw new Error("AES type not supported");
    return await factory.create(key, iv);
}
// #endregion